import { makeAutoObservable, reaction, runInAction, toJS } from 'mobx';
import {
  BrowsingPageType,
  Carrier,
  CartBrowsingCriteria,
  Portfolio,
  ProductLineAttr,
  RateSegment,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { RootStore } from 'store/rootStore';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { Catalog, PortfolioRequest } from 'types/productServiceV2';
import { AssigneeType, Group } from 'types/groupService';
import { INITIAL_GROUP_OBJECT } from 'store/domain/groupStore';
import { ComparePortfolio } from '@insurancemenu/imenu-components-lib/dist/types/ComparisonBlock';
import { Cart } from 'types/cartServiceV2';
import { BrowsingType, DomainProductLine, InputFilter } from 'types/domainServiceV2';
import { INITIAL_CART_OBJECT } from 'store/domain/cartStoreV2';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { ChecklistCompletionStatus, USER_ACTIONS } from 'types/enrollmentService';
import { CopyHelper } from 'global/utils/CopyHelper';
import { QuoteStatus } from 'types/zywaveService';
import { getCart, removePortfolioFromCart } from 'api/operations/cartServiceV2';
import { getCatalog, getPortfolios, getPortfoliosById, wipeRFQPortfolioData } from 'api/operations/productServiceV2';
import { getDomainProductLine } from 'api/operations/domainServiceV2';
import { productServiceHelper } from 'global/services-helpers';
import {
  getAllEnrollmentPeriodsByGroupId,
  getProposalChecklist,
  saveProposalAndCompleteStep,
} from 'api/operations/enrollmentService';
import { getCoveredProductLines } from 'global/services-helpers/enrollmentService';
import { convertNewSaleCartIntoProductLine } from 'global/services-helpers/cartService';
import { INITIAL_CATALOG } from 'global/services-helpers/productService';
import { getQuoteStatus } from 'api/operations/zywaveService';
import { getBrowsingUserActionByRateSegment } from 'global/checklists-helpers/RFQProposalChecklistHelper';
import { getGuidelines } from 'api/operations/uwService';
import { UwGuideline } from 'types/uwService';
import { generateProposal } from 'api/operations/proposalServiceV3';
import { buildInputGroupFromGroup } from 'global/services-helpers/groupService';
import { getRequiredCrossSellViolations, RequiredCrossSellViolation } from 'global/utils/crossSellHelper';
import RequiredCrossSellModalDelegate from './requiredCrossSellModalDelegate';

export interface AllRatesValues {
  [key: string]: number[];
}

export interface PushFn {
  (link: string): void;
}

export default class BrowsingStore {
  rootStore: RootStore;
  loading = false;
  /**
   * List of all attributes for the selected product line with their titles
   */
  attrsForSelectedProductLine: ProductLineAttr[] = [];
  /**
   * Catalog which defines available portfolios and metadata:
   * - productLineAttrs
   * - marketingTags
   * - rateSegment
   */
  catalog: Catalog = INITIAL_CATALOG;
  /**
   * Data of the Portfolios without any filtering that happened on FE
   */
  allPortfolios: Portfolio[] = [];
  /**
   * Data that is needed for displaying portfolio and products info in the card component after filter reduced their amount
   */
  filteredPortfolios: Portfolio[] = [];
  /**
   * List of all product lines for displaying product lines buttons. Note(!) Only Product lines with the same rate segment
   */
  productLines: DomainProductLine[] = [];
  /**
   * Current Cart with items and browsing criteria
   */
  cart: Cart = INITIAL_CART_OBJECT;
  /**
   * List of all compared portfolios
   */
  comparedPortfolios: ComparePortfolio[] = [];
  /**
   * Current group data
   */
  group: Group = {
    ...INITIAL_GROUP_OBJECT,
  };

  /**
   * Used to store min and max values of each Rate Tier
   */
  rateValues: AllRatesValues = {};
  /**
   * Store current active ProductLineId. Used as a confirmed product line, because we also stored this value in the URL
   */
  currentProductLineId?: ProductLineId;
  /**
   * Proposal sidebar opening status
   */
  isProposalSidebarOpened = true;
  /**
   * List of product lines already on a renewal/installed proposal.
   */
  coveredProductLines: ProductLineId[] = [];
  /**
   * Modal that will indicate for the user that we are waiting for rates
   */
  continuousPullingRatesModalOpened = false;
  /**
   * Counter for amount of calls to Zywave, because API is unreliable and our app might stuck on pulling rates
   */
  calculateRatesAttempts = 0;
  /**
   * Indicates whether the RFQ proposal rate step is completed. If completed, additional logic is applied on the first
   * addition to the proposal, which will reset subsequent steps. This helps avoid issues in the release step with
   * portfolios that lack rates if the user does not use the save proposal button after making changes in the Build Proposal step.
   */

  isRateStepCompleted = false;

  uwGuidelines: UwGuideline[] = [];

  requiredCrossSellModal: RequiredCrossSellModalDelegate = new RequiredCrossSellModalDelegate();

  // Setter section
  setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  setCurrentProductLineId(currentProductLineId: ProductLineId): void {
    this.currentProductLineId = currentProductLineId;
  }

  resetBrowsingPageState(): void {
    this.currentProductLineId = undefined;
    this.productLines = [];
    this.attrsForSelectedProductLine = [];
    this.allPortfolios = [];
    this.filteredPortfolios = [];
    this.catalog = INITIAL_CATALOG;
    this.cart = INITIAL_CART_OBJECT;
    this.comparedPortfolios = [];
    this.rateValues = {};
    this.coveredProductLines = [];
    this.uwGuidelines = [];
    this.isRateStepCompleted = false;
    this.continuousPullingRatesModalOpened = false;
    this.calculateRatesAttempts = 0;
  }

  setProposalSidebarOpeningStatus(status: boolean): void {
    this.isProposalSidebarOpened = status;
  }

  setFilteredPortfolios(filteredPortfolios: Portfolio[]): void {
    this.filteredPortfolios = filteredPortfolios;
  }

  setContinuousPullingRatesModal(opened: boolean): void {
    this.continuousPullingRatesModalOpened = opened;
  }

  incrementRatesAttempts(): void {
    this.calculateRatesAttempts += 1;
  }

  setRatesAttempts(value: number): void {
    this.calculateRatesAttempts = value;
  }

  resetRatesAttempts(): void {
    this.calculateRatesAttempts = 0;
  }

  getCalculateRatesAttempts(): number {
    return this.calculateRatesAttempts;
  }

  updateCart(cart: Cart): void {
    this.cart = cart;
  }

  async getGuidelines(productLineId: ProductLineId, effectiveDate: string, bc: CartBrowsingCriteria): Promise<void> {
    this.uwGuidelines = await getGuidelines([{ productLineId, effectiveDate, bc }]);
  }

  // Initial method tha collects all required info for the page
  async collectInitialData(
    groupId: string,
    cartId: string,
    periodId: string,
    pageType: BrowsingPageType,
    productLineIdFromUrl?: ProductLineId | null,
  ): Promise<void> {
    this.setLoading(true);

    const [cart, enrollmentPeriods] = await Promise.all([
      getCart(cartId),
      getAllEnrollmentPeriodsByGroupId(groupId),
      this.getGroupById(groupId),
    ]);

    await runInAction(async () => {
      if (!cart) {
        throw new Error(`cart with such id doesn't exist: ${cartId}`);
      }
      this.cart = CopyHelper.deepCopy(cart);
      this.coveredProductLines = getCoveredProductLines(enrollmentPeriods || []);
      const sponsorId = this.rootStore.userStore.sponsorId;
      const imAgencyId = this.rootStore.userStore.imAgencyId;
      const convertedCart = toJS(this.cart);
      const rateSegment = convertedCart.rateSegment;

      await this.getProductLines(this.cart, rateSegment);

      const entryProductLineId = productLineIdFromUrl ?? this.productLines[0]?.productLineId;
      if (entryProductLineId) {
        this.setCurrentProductLineId(entryProductLineId);
      }

      await runInAction(async () => {
        if (!this.currentProductLineId) {
          throw new Error('No current product line id');
        }
        const requests = [
          this.getCatalog(
            this.cart.lastUpdateBrowsingCriteria.effectiveDate,
            rateSegment,
            this.currentProductLineId,
            this.cart.lastUpdateBrowsingCriteria.state,
          ),
          this.getGuidelines(
            this.currentProductLineId,
            this.cart.lastUpdateBrowsingCriteria.effectiveDate,
            this.cart.lastUpdateBrowsingCriteria,
          ),
        ];
        if (convertedCart.lastUpdateBrowsingCriteria[this.currentProductLineId]) {
          requests.push(
            this.getPortfolios({
              bc: convertedCart.lastUpdateBrowsingCriteria,
              rateSegment,
              sponsorId,
              agencyId: imAgencyId,
              productLineId: this.currentProductLineId,
              groupId,
            }),
          );
        }
        if (pageType === 'rfq') {
          requests.push(this.getRFQProposalChecklist(periodId));
        }
        await Promise.all(requests);
        runInAction(() => {
          if (!this.currentProductLineId) {
            throw new Error('No current product line id');
          }
          const convertedAllPortfolios = this.allPortfolios.map((portfolio) => toJS(portfolio)).slice();
          const convertedProductLineAttrs = this.catalog.productLineAttributes.map((attr) => toJS(attr)).slice();
          const carriersFilter = this.createCarriersFilter(this.currentProductLineId, convertedAllPortfolios);
          // Add the new carrier filter attribute for display in the sidebar filter component when multiple carriers are available
          if (carriersFilter) {
            this.attrsForSelectedProductLine = [...convertedProductLineAttrs, carriersFilter];
          }
          this.setLoading(false);
        });
      });
    });
  }

  async getCatalog(
    effectiveDate: string,
    rateSegment: RateSegment,
    productLineId: ProductLineId,
    state: string,
  ): Promise<void> {
    const res = await getCatalog(this.rootStore.userStore.sponsorId, effectiveDate, rateSegment, state);
    runInAction(() => {
      const catalog = res.data.getCatalog;
      this.catalog = res.data.getCatalog;
      this.attrsForSelectedProductLine = productServiceHelper.getProductLineAttributesFromCatalog(
        catalog,
        productLineId,
      );
    });
  }

  async getPortfolios(request: PortfolioRequest): Promise<void> {
    const res = await getPortfolios(request);
    runInAction(() => {
      const browsingType = this.getProductLineBrowsingType(request.productLineId);

      if (browsingType === 'RFQ') {
        const sortedPortfolios = this.sortCustomPortfolios(res.data.getPortfolios);
        this.allPortfolios = sortedPortfolios;
        this.filteredPortfolios = sortedPortfolios;
      } else {
        this.allPortfolios = res.data.getPortfolios;
        this.filteredPortfolios = res.data.getPortfolios;
      }

      this.rateValues = this.createRates(res.data.getPortfolios);
      if (this.hasMockRates()) this.setContinuousPullingRatesModal(true);
    });
  }

  async getRFQProposalChecklist(periodId: string): Promise<void> {
    const res = await getProposalChecklist(periodId, this.cart.id);
    runInAction(() => {
      const checklist = res.data.getProposalChecklist;
      this.isRateStepCompleted =
        checklist.checklistOptions.find(
          (option) => option.card === getBrowsingUserActionByRateSegment(this.cart.rateSegment).card,
        )?.completionStatus === ChecklistCompletionStatus.COMPLETED;
    });
  }

  // eslint-disable-next-line class-methods-use-this
  sortCustomPortfolios(portfolios: Portfolio[]): Portfolio[] {
    const order: Record<string, number> = {
      'Initial Seed': 1,
      New: 2,
      'In Progress': 3,
      Stock: 4,
    };

    const currentTagName = (tagId: string): string =>
      this.catalog.marketingTags.find((tag) => tag.id === tagId)?.tag || '';
    return portfolios.sort((a, b) => {
      return order[currentTagName(a.marketingTagIds[0])] - order[currentTagName(b.marketingTagIds[0])];
    });
  }

  // eslint-disable-next-line class-methods-use-this
  getAllCarriersFromProducts(portfolios: Portfolio[]): Carrier[] {
    const allCarriers: Carrier[] = [];
    portfolios.forEach(({ products }) => {
      products.forEach(({ carrier }) => {
        if (!allCarriers.find(({ id }) => id === carrier.id)) {
          allCarriers.push(carrier);
        }
      });
    });

    return allCarriers.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
  }

  // eslint-disable-next-line class-methods-use-this
  createCarriersFilter(productLineId: ProductLineId, portfolios: Portfolio[]): ProductLineAttr | undefined {
    const carriers = this.getAllCarriersFromProducts(portfolios);

    const carriersFilter = {
      id: 'carrier',
      displayName: 'carrier',
      productLine: productLineId,
      type: 'carrier',
      category: '',
    };

    return carriers.length > 1 ? carriersFilter : undefined;
  }

  async getGroupById(id: string): Promise<void> {
    await this.rootStore.groupStore.getGroupById(id);
    runInAction(() => {
      this.group = this.rootStore.groupStore.group;
    });
  }

  async getProductLines(cart: Cart, rateSegment: RateSegment): Promise<void> {
    const inputFilter: InputFilter = {
      effectiveDate: cart.lastUpdateBrowsingCriteria.effectiveDate,
      zipCode: cart.lastUpdateBrowsingCriteria.zipCode,
      state: cart.lastUpdateBrowsingCriteria.state,
      county: cart.lastUpdateBrowsingCriteria.county,
      sic: cart.lastUpdateBrowsingCriteria.sic4,
      agencyId: this.rootStore.userStore.imAgencyId,
      sponsorId: this.rootStore.userStore.sponsorId,
    };
    const res = await getDomainProductLine(inputFilter);

    runInAction(() => {
      this.productLines = res
        .filter((line) => line.rateSegment === rateSegment) // filter out product lines that have different rate segment
        .filter(
          (domainLine) =>
            this.rootStore.domainStoreV2.isChangePlanActionAvailable(this.rootStore.userStore.userRoles) ||
            !this.coveredProductLines.includes(domainLine.productLineId), // hide renewal installed lines when the carrier does not allow change-plan
        );
    });
  }

  arePlansSelectedForPLine(pLine: ProductLineId): boolean {
    const convertedCartItems = toJS(this.cart).cartItems;
    return convertedCartItems.filter((cartItem) => cartItem.productLineId === pLine).length > 0;
  }

  getDependentProductLinesForProposal(): ProductLineId[] {
    const convertedProductLines = this.productLines.map((line) => toJS(line)).slice();
    return (
      convertedProductLines
        .filter(
          (pLine) =>
            pLine?.dependentOnProductLineId &&
            this.arePlansSelectedForPLine(pLine.productLineId) &&
            !this.arePlansSelectedForPLine(pLine.dependentOnProductLineId),
        )
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .map((pLine) => pLine.dependentOnProductLineId!)
    );
  }

  // Browsing card methods

  async onAddToProposal(portfolioId: string, portfolioName: string): Promise<void> {
    this.setLoading(true);
    if (!this.currentProductLineId) {
      throw new Error('Unsupported ProductLineId');
    }

    const convertedCart = toJS(this.cart);
    const { id, lastUpdateBrowsingCriteria } = convertedCart;
    const currentPortfolio = this.allPortfolios.find((portfolio) => portfolio.id === portfolioId);
    const convertedPortfolio = toJS(currentPortfolio);
    const bcVersion = convertedPortfolio && Object.values(convertedPortfolio.bcVersions)[0];

    const res = await this.rootStore.cartStoreV2.addPortfolioToCart({
      cartId: id,
      portfolioId,
      portfolioName,
      browsingCriteria: { ...lastUpdateBrowsingCriteria, ...bcVersion },
      productLineId: this.currentProductLineId,
      bcIndex: null,
    });
    runInAction(() => {
      const currentCart = res.data?.addPortfolioConfigToCart;
      if (currentCart) this.cart = currentCart;
      this.setLoading(false);
    });
  }

  async onAddToRFQProposal(portfolioId: string, portfolioName: string, periodId: string): Promise<void> {
    this.setLoading(true);

    await this.onAddToProposal(portfolioId, portfolioName);
    void runInAction(async () => {
      // Complete the RFQ step again to reset the process, preventing continuation with portfolios without rates
      if (this.isRateStepCompleted) {
        await this.confirmSaveCustomProposal(this.cart, periodId, USER_ACTIONS.COMPLETE_BUILD_RFQ);
        this.setLoading(false);
      }
    });
  }

  // isLast === true -> remove last bc (click on remove button on card )
  // isLast === false -> remove cart item from right sidebar
  async onRemoveFromProposal(portfolioId: string, isLast: boolean, type: BrowsingPageType): Promise<void> {
    const isRFQType = type === 'rfq';
    this.setLoading(true);
    const res = await removePortfolioFromCart(this.cart.id, portfolioId, isRFQType ? false : isLast);
    void runInAction(async () => {
      const currentCart = res.data?.removePortfolioConfigFromCart;
      if (currentCart) this.cart = currentCart;

      if (isRFQType) {
        // Reset all versions from portfolio
        await wipeRFQPortfolioData(this.cart.id, portfolioId);
      }

      this.setLoading(false);
    });
  }

  async checkZywaveQuotingStatus(): Promise<QuoteStatus> {
    if (!this.currentProductLineId) {
      throw new Error('Unsupported ProductLineId');
    }
    return getQuoteStatus(this.group.id, this.currentProductLineId, this.cart.lastUpdateBrowsingCriteria);
  }

  // Functional section

  // eslint-disable-next-line class-methods-use-this
  createRates(portfolios: Portfolio[]): AllRatesValues {
    // get all values for each rate
    const allRatesValues: AllRatesValues = {};

    portfolios.forEach((portfolio: Portfolio) => {
      portfolio.products.forEach((product) => {
        Object.values(product.bcDependentData[0].rates).forEach(({ displayKey, value }) => {
          if (allRatesValues[displayKey]) {
            allRatesValues[displayKey] = [...allRatesValues[displayKey], value];
          } else {
            allRatesValues[displayKey] = [value];
          }
        });
      });
    });

    // get min and max value for each rate
    const convertedRates: AllRatesValues = {};

    Object.entries(allRatesValues).forEach(([key, values]) => {
      const maxValue = Math.max(...values);
      const minValue = Math.min(...values);
      convertedRates[key] = [Math.floor(minValue), Math.ceil(maxValue)];
    });
    return convertedRates;
  }

  // eslint-disable-next-line class-methods-use-this
  onViewPlanDetails(portfolioId: string, cartId: string, pushFn: PushFn): void {
    pushFn(
      `/product-compare?type=details&portfolioId=${portfolioId}&cartId=${cartId}&productLineId=${this.currentProductLineId}`,
    );
  }

  onCompare(id: string, compareStatus: boolean): void {
    if (compareStatus) {
      const currentPortfolio = this.allPortfolios.find((portfolio) => {
        return portfolio.id === id;
      });

      if (currentPortfolio) {
        this.comparedPortfolios = [
          ...this.comparedPortfolios,
          {
            id: currentPortfolio.id,
            logoUrl: currentPortfolio.products[0].carrier.logo, // Take carrier logo in the first product in portfolio
            portfolioName: currentPortfolio.name,
          },
        ];
        return;
      }
    }
    this.onRemoveFromComparison(id);
  }

  onRemoveFromComparison(id: string): void {
    this.comparedPortfolios = this.comparedPortfolios.filter((portfolio) => id !== portfolio.id);
  }

  clearComparedPortfolios(): void {
    this.comparedPortfolios = [];
  }

  hasMockRates(): boolean {
    return this.allPortfolios.some((portfolio) =>
      portfolio.products.some((product) => product.bcDependentData[0].rates.some((rate) => rate.key === 'MOCK')),
    );
  }

  // eslint-disable-next-line class-methods-use-this
  compareSelectedPortfolios(pushFn: PushFn): void {
    const comparedPortfolioIds = this.comparedPortfolios.map(({ id }) => id).join(',');
    pushFn(
      `/product-compare?type=portfolios&portfolioIds=${comparedPortfolioIds}&cartId=${this.cart.id}&productLineId=${this.currentProductLineId}`,
    );
    this.clearComparedPortfolios();
  }

  // eslint-disable-next-line class-methods-use-this
  compareAllPortfolios(pushFn: PushFn): void {
    pushFn(`/product-compare?type=all-portfolios&cartId=${this.cart.id}&productLineId=${this.currentProductLineId}`);
    this.clearComparedPortfolios();
  }

  // eslint-disable-next-line class-methods-use-this
  compareVariants(portfolioId: string, cartId: string, pushFn: PushFn): void {
    pushFn(
      `/product-compare?type=variants&portfolioId=${portfolioId}&cartId=${cartId}&productLineId=${this.currentProductLineId}`,
    );
    this.clearComparedPortfolios();
  }

  compareAllPortfoliosInPl(productLineId: ProductLineId, pushFn: PushFn): void {
    pushFn(`/product-compare?type=cart&cartId=${this.cart.id}&productLineId=${productLineId}`);
    this.clearComparedPortfolios();
  }

  onConfigurePortfolio(portfolioId: string, cartId: string, pushFn: PushFn): void {
    pushFn(
      `/product-compare?type=config-portfolio&portfolioId=${portfolioId}&cartId=${cartId}&productLineId=${this.currentProductLineId}`,
    );
    this.clearComparedPortfolios();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, class-methods-use-this
  onContinueChecklist(
    params: { templateId: string; periodId: string; cartId: string; groupId: string },
    pushFn: PushFn,
  ): void {
    const { templateId, ...restParams } = params;
    pushFn(`/product-template-checklist/${templateId}?type=browsing`);

    // Set necessary parameters in local storage to persist them across the product template pages
    localStorage.setItem('params', JSON.stringify(restParams));
  }

  async confirmSaveCustomProposal(cart: Cart, periodId: string, userAction: USER_ACTIONS): Promise<void> {
    const inputProductLines = convertNewSaleCartIntoProductLine(cart);
    this.setLoading(true);

    await saveProposalAndCompleteStep(
      {
        groupId: cart.groupId,
        cartName: cart.cartName,
        cartId: cart.id,
        effectiveDate: cart.lastUpdateBrowsingCriteria.effectiveDate,
        productLines: inputProductLines,
      },
      periodId,
      userAction,
      cart.rateSegment,
    );
    this.setLoading(false);
  }

  async confirmSaveQuoteProposal(periodId: string): Promise<void> {
    this.setLoading(true);
    const inputProductLines = convertNewSaleCartIntoProductLine(this.cart);

    const inputGroup = buildInputGroupFromGroup(this.group);
    const {
      lastUpdateBrowsingCriteria: { zipCode: zip, state, county, effectiveDate },
      groupId,
      cartName,
      id: cartId,
      rateSegment,
      cartItems,
    } = this.cart;

    // Update address in input group
    inputGroup.contactInfo.primaryAddress = {
      ...inputGroup.contactInfo.primaryAddress,
      zip,
      state,
      county,
    };

    const [savedPeriod, portfolios] = await Promise.all([
      saveProposalAndCompleteStep(
        {
          groupId,
          cartName,
          cartId,
          effectiveDate,
          productLines: inputProductLines,
        },
        periodId,
        getBrowsingUserActionByRateSegment(rateSegment).userActions,
        rateSegment,
      ),
      getPortfoliosById(cartItems, undefined, groupId, cartId, 'no-cache'),
      this.rootStore.groupStore.updateGroup(inputGroup),
    ]);

    if (savedPeriod) {
      await runInAction(async () => {
        await generateProposal({
          domainId: this.rootStore.userStore.sponsorId,
          catalogId: this.catalog.id,
          cart: this.cart,
          portfolios,
          period: savedPeriod,
          // Old groups may have null agentOfRecord (AOR was not required when they were created).
          group: {
            ...this.group,
            agentOfRecord: this.group.agentOfRecord ?? { id: '', name: '', type: AssigneeType.AGENT },
          },
        });

        this.setLoading(false);
      });
    }
  }

  getProductLineBrowsingType(productLineId?: ProductLineId): BrowsingType {
    const currentProductLine = this.productLines.find((productLine) => productLine.productLineId === productLineId);
    return currentProductLine?.browsingType || 'SHELF';
  }

  public get unusedProductLineIds(): ProductLineId[] {
    const usedProductLineIds = new Set(this.cart.cartItems.map((item) => item.productLineId));
    return this.productLines.map((pl) => pl.productLineId).filter((id) => !usedProductLineIds.has(id));
  }

  private get _requiredCrossSellViolations(): RequiredCrossSellViolation[] {
    return getRequiredCrossSellViolations(this.cart.cartItems);
  }

  public get areRequiredCrossSellPlansAdded(): boolean {
    return this._requiredCrossSellViolations.length === 0;
  }

  public openRequiredCrossSellModal(): void {
    void this.requiredCrossSellModal.openModal(this._requiredCrossSellViolations);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });

    reaction(
      () => this.continuousPullingRatesModalOpened,
      (modalOpened) => {
        if (modalOpened) {
          // create request for the Portfolios outside interval to avoid warning that it's not in reactive context
          const productLineId = this.currentProductLineId || 'GroupMedical';
          const request: PortfolioRequest = {
            bc: this.cart.lastUpdateBrowsingCriteria,
            rateSegment: PRODUCT_LINES_CONTENT_MAP[productLineId].rateSegment,
            sponsorId: this.rootStore.userStore.sponsorId,
            agencyId: this.rootStore.userStore.imAgencyId,
            productLineId,
            groupId: this.group.id,
          };
          // if rates mocked, that means that it's Zywave plans, so we have to wait
          // until they will calculate rates. Code below make calls to ZywaveService
          // each 5 seconds and stops, only when the response is RATED, or it was more
          // than 10 attempts
          const interval = setInterval(() => {
            this.checkZywaveQuotingStatus()
              .then(async (status) => {
                if (status === QuoteStatus.RATED) {
                  clearInterval(interval);

                  // After rates calculated we have to update portfolio rates,
                  // so here we make NO-CACHE request for portfolios,
                  // otherwise u will receive mock rates again
                  this.setRatesAttempts(-1);
                  await this.getPortfolios(request);
                  this.setContinuousPullingRatesModal(false);
                } else if (this.getCalculateRatesAttempts() < 10) {
                  this.incrementRatesAttempts();
                } else {
                  throw new Error('Too many attempts to rate Quote');
                }
              })
              .catch((error) => {
                this.setContinuousPullingRatesModal(false);
                clearInterval(interval);
                throw new Error(error as string);
              });
          }, 5000);
        }
      },
    );
  }
}
